import React, { FC, MouseEvent } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import * as Core from '../../core';

type IconProp =
  | IconName
  | {
      icon: IconName;
      ariaLabel?: string;
    };

export interface Props {
  className?: string;
  icon?: IconProp;
  iconClick?: (event: MouseEvent) => void;
  grabberClick?: (event: MouseEvent) => void;
  leftIconClick?: (event: MouseEvent) => void;
  label?: string;
  rightIcon?: IconProp;
  type?: 'info' | 'error' | 'warning' | 'success' | 'generic' | 'outline';
  size?: Core.TagSize;
  isClosable?: boolean;
}

export const AttentionGrabber: FC<Props> = ({
  type = 'generic',
  grabberClick,
  label,
  icon,
  iconClick,
  rightIcon,
  size = Core.TagSize.MEDIUM,
  leftIconClick,
  isClosable,
  className,
}) => {
  const colorScheme = type === 'generic' || type === 'outline' ? 'neutral' : type;

  const getIconName = (icon?: IconProp): IconName | undefined => (typeof icon === 'string' ? icon : icon?.icon);

  const getAriaLabel = (icon?: IconProp): string =>
    typeof icon === 'object' && icon?.ariaLabel ? icon.ariaLabel : 'action-button';

  const leftIconButton =
    leftIconClick && icon ? (
      <Core.IconButton
        icon={{ iconName: getIconName(icon)! }}
        ariaLabel={getAriaLabel(icon)}
        onClick={leftIconClick}
        variant="link"
        colorScheme={colorScheme}
        size="xs"
        minW="unset"
      />
    ) : null;

  const rightIconButton =
    iconClick && rightIcon ? (
      <Core.IconButton
        icon={{ iconName: getIconName(rightIcon)! }}
        ariaLabel={getAriaLabel(rightIcon)}
        onClick={iconClick}
        variant="link"
        colorScheme={colorScheme}
        size="xs"
        minW="unset"
      />
    ) : null;

  // If icons are non-clickable, prepare icon props for Core.Tag
  const leftIconProp = leftIconClick ? undefined : icon ? { iconName: getIconName(icon)! } : undefined;
  const rightIconProp = iconClick ? undefined : rightIcon ? { iconName: getIconName(rightIcon)! } : undefined;

  return (
    <Core.Tag
      colorScheme={colorScheme}
      variant={type === 'outline' ? Core.TagVariant.outline : Core.TagVariant.subtle}
      onClick={grabberClick}
      size={size}
      tagLabel={!(leftIconButton && rightIconButton) ? label : undefined}
      isClosable={isClosable}
      leftIcon={leftIconProp}
      rightIcon={rightIconProp}
      className={className}
    >
      {leftIconButton}
      {leftIconButton && rightIconButton && label}
      {rightIconButton}
    </Core.Tag>
  );
};

AttentionGrabber.displayName = 'AttentionGrabber';
